/**
 * EditClientForm Component
 * Enhancement: Edit client fields inline when "Change" is clicked
 * 
 * Allows editing existing client's company name, contact name, email, phone, and address
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormInput } from '../shared/FormInput';
import type { ClientInfo } from '../../types';

// Schema for editing client
const editClientSchema = z.object({
  companyName: z.string().min(1, 'Company name is required').max(255),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Invalid email address').max(255),
  phone: z.string().max(20).optional().or(z.literal('')),
  country: z.string().min(1, 'Country is required').max(100),
  city: z.string().max(100).optional().or(z.literal('')),
  street: z.string().max(255).optional().or(z.literal('')),
  status: z.enum(['ACTIVE', 'ARCHIVED']), // Feature 019: Client status management
  clientSegment: z.enum(['BeautyProClinics', 'DropshipperReseller', 'Corporate', 'BrandOwner', 'InfluencerCreator', 'Other']).optional(), // Feature 019: Client segmentation
});

export type EditClientFormData = z.infer<typeof editClientSchema>;

export interface EditClientFormProps {
  clientInfo: ClientInfo;
  onSubmit: (data: EditClientFormData) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function EditClientForm({
  clientInfo,
  onSubmit,
  onCancel,
  isLoading = false,
}: EditClientFormProps) {
  // Parse contact name into first and last name (simple split)
  const [firstName = '', lastName = ''] = (clientInfo.contactName || '').split(' ', 2);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditClientFormData>({
    resolver: zodResolver(editClientSchema),
    defaultValues: {
      companyName: clientInfo.companyName || '',
      firstName: firstName,
      lastName: lastName,
      email: clientInfo.email || '',
      phone: clientInfo.phone || '',
      country: 'USA', // Default since we don't have it in ClientInfo
      city: '', // Not available in ClientInfo
      street: '', // Not available in ClientInfo
      status: clientInfo.status, // Feature 019: Include current status
      clientSegment: clientInfo.clientSegment as any || undefined, // Feature 019: Include client segment
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Edit Client</h3>

      {/* Company Name - Required */}
      <FormInput
        label="Company Name"
        required
        error={errors.companyName?.message}
        {...register('companyName')}
      />

      {/* First Name - Required */}
      <FormInput
        label="First Name"
        required
        placeholder="Primary contact's first name"
        error={errors.firstName?.message}
        {...register('firstName')}
      />

      {/* Last Name - Required */}
      <FormInput
        label="Last Name"
        required
        placeholder="Primary contact's last name"
        error={errors.lastName?.message}
        {...register('lastName')}
      />

      {/* Email - Required */}
      <FormInput
        label="Email"
        type="email"
        required
        error={errors.email?.message}
        {...register('email')}
      />

      {/* Phone */}
      <FormInput
        label="Phone"
        type="tel"
        error={errors.phone?.message}
        {...register('phone')}
      />

      {/* Country */}
      <FormInput
        label="Country"
        required
        error={errors.country?.message}
        {...register('country')}
      />

      {/* City */}
      <FormInput
        label="City"
        error={errors.city?.message}
        {...register('city')}
      />

      {/* Street */}
      <FormInput
        label="Street Address"
        error={errors.street?.message}
        {...register('street')}
      />

      {/* Status - Feature 019: FR-014 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
          <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          {...register('status')}
        >
          <option value="ACTIVE">Active</option>
          <option value="ARCHIVED">Inactive</option>
        </select>
        {errors.status && (
          <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
        )}
      </div>

      {/* Account Segment - Feature 019 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Account Segment
        </label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          {...register('clientSegment')}
        >
          <option value="">Select segment...</option>
          <option value="BeautyProClinics">Beauty pro / Clinics</option>
          <option value="DropshipperReseller">Dropshipper / Reseller</option>
          <option value="Corporate">Corporate</option>
          <option value="BrandOwner">Brand owner</option>
          <option value="InfluencerCreator">Influencer / Creator</option>
          <option value="Other">Other</option>
        </select>
        {errors.clientSegment && (
          <p className="mt-1 text-sm text-red-600">{errors.clientSegment.message}</p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
